import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "cx-date-time-picker" }

import { ref, computed, ComputedRef, } from 'vue';
import VueDatePicker, { DatePickerMarker, DatePickerInstance } from '@vuepic/vue-datepicker';
import CxDatePickerActivator from './CxDatePickerActivator.vue';
import CxMonthCell from './CxMonthCell.vue';
import CxYearCell from './CxYearCell.vue';
import '@vuepic/vue-datepicker/dist/main.css';
import datetimehelper from '@/utils/datetimehelper';
import { endOfDay } from 'date-fns';

interface Props {
  modelValue?: Date | null,
  minDate?: Date,
  maxDate?: Date
  events?: Date[],
  autoApply?: boolean,
  keepActionRow?: boolean,
  closeOnAutoApply?: boolean,
  dateFormat?: string,
  disabled?: boolean,
  enableTimePicker?: boolean,
  hideOnClickOutside?: boolean,
  loading?: boolean,
  placeholder?: string,
  label?: string,
  allowFutureDates?: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CxDateTimePicker',
  props: {
    modelValue: { default: () => new Date() },
    minDate: {},
    maxDate: {},
    events: { default: () => [] },
    autoApply: { type: Boolean, default: false },
    keepActionRow: { type: Boolean, default: false },
    closeOnAutoApply: { type: Boolean, default: true },
    dateFormat: {},
    disabled: { type: Boolean, default: false },
    enableTimePicker: { type: Boolean, default: true },
    hideOnClickOutside: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    placeholder: {},
    label: {},
    allowFutureDates: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "toggleDatePickerOpen", "onApply"],
  setup(__props: any, { emit }) {

const props = __props;





const datepicker = ref<DatePickerInstance>(null);

const updateModelValue = (newValue: Date) => {
  emit('onApply', newValue);
  emit('update:modelValue', newValue);
};

const innerMaxDate = computed(() => {
  if (props.allowFutureDates) {
    return props.maxDate ? endOfDay(props.maxDate) : undefined;
  }
  return new Date();
});

const innerDateFormat = computed(() => {
  if (props.dateFormat) {
    return props.dateFormat;
  }
  if (props.enableTimePicker) {
    return datetimehelper.LOCALE_DATE_TIME_FORMAT;
  }
  return datetimehelper.LOCALE_DATE_FORMAT;
});

const dateFormatter = (dateToFormat: Date) => {
  return datetimehelper.format(dateToFormat, innerDateFormat.value);
};

const selectedMonth = ref<number>(props.modelValue === null ? new Date().getMonth() : props.modelValue.getMonth());
const selectedYear = ref<number>(props.modelValue === null ? new Date().getFullYear() : props.modelValue.getFullYear());

const activeDays: ComputedRef<DatePickerMarker[]> = computed(() => props
  .events.map(event => ({ date: event, type: 'dot' })));

const isActiveMonth = (month: number) => {
  const activeMonths = activeDays.value
    .filter(a => new Date(a.date).getFullYear() === selectedYear.value)
    .map(a => new Date(a.date).getMonth());
  return activeMonths.includes(month);
};

const isActiveYear = (year: number) => {
  const activeYears = activeDays.value
    .map(a => new Date(a.date).getFullYear());
  return activeYears.includes(year);
};

const handleChangeMonthYear = ({ month, year }: { month: number, year: number }) => {
  selectedMonth.value = month;
  selectedYear.value = year;
};

const resetSelectedMonthYear = () => {
  selectedMonth.value = props.modelValue === null ? new Date().getMonth() : props.modelValue.getMonth();
  selectedYear.value = props.modelValue === null ? new Date().getFullYear() : props.modelValue.getFullYear();
  emit('toggleDatePickerOpen', true);
};

const onCloseDatePicker = () => {
  emit('toggleDatePickerOpen', false);
};

const handleClickOutside = () => {
  if (props.hideOnClickOutside && datepicker.value) {
    datepicker.value.closeMenu();
  }
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(VueDatePicker), {
      id: "date-time-picker",
      ref_key: "datepicker",
      ref: datepicker,
      dark: true,
      preventMinMaxNavigation: true,
      modelValue: _ctx.modelValue,
      minDate: _ctx.minDate,
      maxDate: innerMaxDate.value,
      range: false,
      markers: activeDays.value,
      autoApply: _ctx.autoApply,
      keepActionRow: _ctx.keepActionRow,
      closeOnAutoApply: _ctx.closeOnAutoApply,
      format: dateFormatter,
      clearable: false,
      disabled: _ctx.disabled || _ctx.loading,
      enableTimePicker: _ctx.enableTimePicker,
      timePickerInline: true,
      onClickOutside: handleClickOutside,
      onUpdateMonthYear: handleChangeMonthYear,
      "onUpdate:modelValue": updateModelValue,
      onOpen: resetSelectedMonthYear,
      onClosed: onCloseDatePicker
    }, {
      "dp-input": _withCtx(({ value }) => [
        _renderSlot(_ctx.$slots, "activator", { value: value }, () => [
          _createVNode(CxDatePickerActivator, {
            value: value,
            disabled: _ctx.disabled,
            loading: _ctx.loading,
            placeholder: _ctx.placeholder,
            label: _ctx.label
          }, null, 8, ["value", "disabled", "loading", "placeholder", "label"])
        ])
      ]),
      "month-overlay-value": _withCtx(({ value, text }) => [
        _createVNode(CxMonthCell, {
          monthText: text,
          monthValue: value,
          isActiveMonth: isActiveMonth(value),
          selectedYear: selectedYear.value
        }, null, 8, ["monthText", "monthValue", "isActiveMonth", "selectedYear"])
      ]),
      "year-overlay-value": _withCtx(({ value }) => [
        _createVNode(CxYearCell, {
          yearValue: value,
          isActiveYear: isActiveYear(value)
        }, null, 8, ["yearValue", "isActiveYear"])
      ]),
      _: 3
    }, 8, ["modelValue", "minDate", "maxDate", "markers", "autoApply", "keepActionRow", "closeOnAutoApply", "disabled", "enableTimePicker"])
  ]))
}
}

})